import { useEffect, lazy, Suspense } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useStream } from "@/contexts/StreamContext";
import { usePersona } from "@/contexts/PersonaContext";
import { ScheduleProvider } from "@/contexts/ScheduleContext";
import { useTheme } from "@/hooks/useTheme";
import { useRouterHistory } from "@/hooks/useRouterHistory";
import { useScheduleHandlers } from "@/hooks/useScheduleHandlers";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import SynqusLogo from "@/components/SynqusLogo";
import { ChatBubble } from "@/components/ChatBubble";

import "@/styles/global.css";

import type { ViewName, OrgUser } from "@/types";

// Lazy-loaded views (code splitting)
const LoginView = lazy(() => import("@/views/LoginView"));
const LandingView = lazy(() => import("@/views/LandingView"));
const OnboardingView = lazy(() => import("@/views/OnboardingView"));
const StreamSelectView = lazy(() => import("@/views/StreamSelectView"));
const PersonaSelectView = lazy(() => import("@/views/PersonaSelectView"));
const CategoryView = lazy(() => import("@/views/CategoryView"));
const SubcategoryView = lazy(() => import("@/views/SubcategoryView"));
const ConfigureView = lazy(() => import("@/views/ConfigureView"));
const ConfirmView = lazy(() => import("@/views/ConfirmView"));
const DoneView = lazy(() => import("@/views/DoneView"));
const AnalyticsView = lazy(() => import("@/views/AnalyticsView"));
const SmartLinksView = lazy(() => import("@/views/SmartLinksView"));
const AvailabilityPrefsView = lazy(() => import("@/views/AvailabilityPrefsView"));
const CalendarConnectionsView = lazy(() => import("@/views/CalendarConnectionsView"));
const CalendarOverlayView = lazy(() => import("@/views/CalendarOverlayView"));
const PublicBookingView = lazy(() => import("@/views/PublicBookingView"));
const TeamPagesView = lazy(() => import("@/views/TeamPagesView"));
const PublicTeamPageView = lazy(() => import("@/views/PublicTeamPageView"));
const CirclesView = lazy(() => import("@/views/CirclesView"));
const JoinCircleView = lazy(() => import("@/views/JoinCircleView"));
const PricingView = lazy(() => import("@/views/PricingView"));

// ─── Loading Fallback ─────────────────────────────────────────────────────
function ViewLoader() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "inherit",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <SynqusLogo size={48} idPrefix="lazy-load" style={{ animation: "pulse 1.5s ease infinite" }} />
        <div style={{ marginTop: 12, color: "#9ca3af", fontSize: 13 }}>Loading...</div>
      </div>
    </div>
  );
}

function AppContent() {
  // ─── ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS ────────
  const { theme, colors: c, setAccentColor } = useTheme();
  const { authState, provider, calendarProvider } = useAuth();
  const { stream } = useStream();
  const { persona } = usePersona();

  useRouterHistory();
  const {
    state,
    dispatch,
    isInterview,
    slots,
    orgUsersAsTeamMembers,
    selectCategory,
    selectSubcategory,
    copyLink,
    switchPreview,
    book,
    confirm,
    handleNlpAction,
    reset,
  } = useScheduleHandlers();

  // ─── LOAD ORG DIRECTORY ───────────────────────────────────────
  useEffect(() => {
    if (authState !== "loggedIn") return;
    const load = async () => {
      try {
        const res = await fetch(provider === "google" ? "/api/google/users" : "/api/users", { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          const users = Array.isArray(data) ? data : data.value || [];
          dispatch({ type: "SET_ORG_USERS", users });
        }
      } catch (e) {
        console.error("Failed to load org users:", e);
      }
    };
    load();
  }, [authState, provider, dispatch]);

  // ─── SYNC PERSONA ACCENT COLOR ────────────────────────────────
  useEffect(() => {
    if (persona?.accentColor && setAccentColor) {
      setAccentColor(persona.accentColor);
    }
  }, [persona, setAccentColor]);

  // ─── HANDLE STRIPE CHECKOUT RETURN ────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    if (checkout === "success") {
      // Clean up the URL
      window.history.replaceState({}, "", window.location.pathname);
      // Optionally show a success toast — the plan will update via webhook
    } else if (checkout === "cancelled") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // ─── PUBLIC PAGES — no auth required ──────────────────────────
  const path = window.location.pathname;
  if (path.startsWith("/book/")) {
    const linkId = path.split("/book/")[1];
    return <PublicBookingView linkId={linkId} />;
  }
  if (path.startsWith("/team/")) {
    const slug = path.split("/team/")[1];
    return (
      <Suspense fallback={<ViewLoader />}>
        <PublicTeamPageView slug={slug} />
      </Suspense>
    );
  }
  if (path.startsWith("/join-circle/")) {
    const code = path.split("/join-circle/")[1];
    return (
      <Suspense fallback={<ViewLoader />}>
        <JoinCircleView code={code} />
      </Suspense>
    );
  }

  // ─── AUTH GATE ────────────────────────────────────────────────
  if (authState === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: c.bg,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <SynqusLogo size={64} idPrefix="loading" style={{ animation: "pulse 1.5s ease infinite" }} />
          <div style={{ marginTop: 16, color: c.tx2, fontSize: 13 }}>Connecting...</div>
        </div>
      </div>
    );
  }

  if (authState === "loggedOut")
    return (
      <Suspense fallback={<ViewLoader />}>
        <LoginView />
      </Suspense>
    );

  // Show onboarding for email users who haven't connected a calendar
  if (authState === "loggedIn" && provider === "email" && !calendarProvider) {
    return (
      <Suspense fallback={<ViewLoader />}>
        <OnboardingView />
      </Suspense>
    );
  }

  if (!stream && authState === "loggedIn")
    return (
      <Suspense fallback={<ViewLoader />}>
        <StreamSelectView />
      </Suspense>
    );
  if (!persona && authState === "loggedIn")
    return (
      <Suspense fallback={<ViewLoader />}>
        <PersonaSelectView />
      </Suspense>
    );

  // ─── VIEW ROUTER ──────────────────────────────────────────────
  const renderView = () => {
    if (state.view === "landing") {
      return (
        <ErrorBoundary section="Landing">
          <LandingView onNavigate={(v) => dispatch({ type: "SET_VIEW", view: v as ViewName })} />
        </ErrorBoundary>
      );
    }
    if (state.view === "analytics") {
      return (
        <ErrorBoundary section="Analytics">
          <AnalyticsView />
        </ErrorBoundary>
      );
    }
    if (state.view === "links") {
      return (
        <ErrorBoundary section="Smart Links">
          <SmartLinksView onBack={() => dispatch({ type: "SET_VIEW", view: "landing" })} />
        </ErrorBoundary>
      );
    }
    if (state.view === "preferences") {
      return (
        <ErrorBoundary section="Availability Preferences">
          <AvailabilityPrefsView />
        </ErrorBoundary>
      );
    }
    if (state.view === "calendars") {
      return (
        <ErrorBoundary section="Calendar Connections">
          <CalendarConnectionsView />
        </ErrorBoundary>
      );
    }
    if (state.view === "team-pages") {
      return (
        <ErrorBoundary section="Team Pages">
          <TeamPagesView onBack={() => dispatch({ type: "SET_VIEW", view: "landing" })} />
        </ErrorBoundary>
      );
    }
    if (state.view === "circles") {
      return (
        <ErrorBoundary section="Circles">
          <CirclesView onBack={() => dispatch({ type: "SET_VIEW", view: "landing" })} />
        </ErrorBoundary>
      );
    }
    if (state.view === "pricing") {
      return (
        <ErrorBoundary section="Pricing">
          <PricingView onBack={() => dispatch({ type: "SET_VIEW", view: "landing" })} />
        </ErrorBoundary>
      );
    }
    if (state.view === "pick" && !state.category) {
      return (
        <ErrorBoundary section="Category Selection">
          <CategoryView onSelectCategory={selectCategory} />
        </ErrorBoundary>
      );
    }
    if (state.view === "pick" && state.category && !state.subcategory) {
      return (
        <ErrorBoundary section="Subcategory Selection">
          <SubcategoryView
            category={state.category}
            onSelectSubcategory={selectSubcategory}
            onResetCategory={() => dispatch({ type: "SELECT_CATEGORY", category: null })}
          />
        </ErrorBoundary>
      );
    }
    if (state.view === "overlay" && state.category && state.subcategory && state.sel.length > 0) {
      const selectedMembers = orgUsersAsTeamMembers.filter((u) => state.sel.includes(u.id));
      return (
        <ErrorBoundary section="Calendar Overlay">
          <CalendarOverlayView
            members={selectedMembers}
            duration={state.dur?.mins || 30}
            onSelectSlot={(start, end) => {
              // Create a synthetic TimeSlot from the selected times
              const startMs = new Date(start).getTime();
              const endMs = new Date(end).getTime();
              const slot: (typeof slots)[0] = {
                utcStart: startMs / 3600000,
                utcEnd: endMs / 3600000,
                hasTentativeOverlap: false,
                perPerson: selectedMembers.map((m) => ({
                  member: m,
                  localStart: startMs / 3600000,
                  localEnd: endMs / 3600000,
                  loc: { city: "", flag: "", utc: 0, tz: "UTC", work: [8, 18] as [number, number] },
                })),
              };
              book(slot);
            }}
            onBack={() => dispatch({ type: "SET_VIEW", view: "pick" })}
          />
        </ErrorBoundary>
      );
    }
    if (state.view === "pick" && state.category && state.subcategory) {
      return (
        <ErrorBoundary section="Calendar Configuration">
          <ConfigureView slots={slots} users={orgUsersAsTeamMembers} onBook={book} onCopyLink={copyLink} />
        </ErrorBoundary>
      );
    }
    if (state.view === "confirm" && state.chosen) {
      return (
        <ErrorBoundary section="Booking Confirmation">
          <ConfirmView
            previewTab={state.previewTab}
            previewPerson={state.previewPerson}
            interviewerTpl={state.interviewerTpl}
            candidateTpl={state.candidateTpl}
            onSetPreviewTab={(t) => dispatch({ type: "SET_FIELD", field: "previewTab", value: t })}
            onSwitchPreview={switchPreview}
            onSetInterviewerTpl={(t) => dispatch({ type: "SET_FIELD", field: "interviewerTpl", value: t })}
            onSetCandidateTpl={(t) => dispatch({ type: "SET_FIELD", field: "candidateTpl", value: t })}
            onConfirm={confirm}
            onBack={() => dispatch({ type: "SET_VIEW", view: "pick" })}
          />
        </ErrorBoundary>
      );
    }
    if (state.view === "done" && state.chosen) {
      return (
        <ErrorBoundary section="Done">
          <DoneView
            category={state.category!}
            subcategory={state.subcategory!}
            isInterview={isInterview}
            chosen={state.chosen}
            title={state.title}
            candidate={state.candidate}
            candidateEmail={state.candidateEmail}
            position={state.position}
            notes={state.notes}
            sentEmails={state.sentEmails}
            onReset={reset}
          />
        </ErrorBoundary>
      );
    }
    return null;
  };

  return (
    <>
      <Suspense fallback={<ViewLoader />}>{renderView()}</Suspense>
      <ErrorBoundary section="Chat">
        <ChatBubble users={state.orgUsers as OrgUser[]} onAction={handleNlpAction} theme={theme} />
      </ErrorBoundary>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ScheduleProvider>
        <AppContent />
      </ScheduleProvider>
    </ErrorBoundary>
  );
}
